import { createSlice } from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";

import * as actions from "./Actions";

const initialState = {
  isLoading: false,
  error: "",
  success: false,
  data: {},
  planDetails: [],
  nutritionistData: {},
  stepsData: {},
  footerCard: {},
  nutritionOnboardingId: null,
  // Question modules state
  questionModulesLoading: false,
  questionModulesError: "",
  questionModulesSuccess: false,
  questionModulesData: {},
  questionTypes: [],
  nextQuestionModuleId: null,
  // Questionnaire state
  activeCategoryIds: [],
  currentCategoryIndex: 0,
  questionsByCategory: {},
  questionsLoading: false,
  questionsError: "",
  savingAnswers: false,
  savingError: "",
  savingProgress: { current: 0, total: 0 },
  // Validation state
  validationErrors: {},
};

const nutritionOnboardingSlice = createSlice({
  name: "nutritionOnboarding",
  initialState,
  reducers: {
    setCategories: (state, { payload }) => {
      state.activeCategoryIds = payload;
      state.currentCategoryIndex = 0;
    },
    setCurrentCategoryIndex: (state, { payload }) => {
      state.currentCategoryIndex = payload;
      
      // Console log current store data when navigating
      console.debug('=== NAVIGATION - STORE DATA ===');
      console.debug('New Category Index:', payload);
      console.debug('Questions by Category:', JSON.stringify(state.questionsByCategory, null, 2));
      console.debug('Active Category IDs:', state.activeCategoryIds);
      console.debug('Nutrition Onboarding ID:', state.nutritionOnboardingId);
      console.debug('===============================');
    },
    updateAnswer: (state, { payload }) => {
      const { categoryId, questionId, answer } = payload;
      if (state.questionsByCategory[categoryId]) {
        const question = state.questionsByCategory[categoryId].find(q => q.id === questionId);
        if (question) {
          question.customer_answer = answer;
        }
      }
      
      // Console log current store data for debugging
      console.debug('=== NUTRITION ONBOARDING STORE DATA ===');
      console.debug('Questions by Category:', JSON.stringify(state.questionsByCategory, null, 2));
      console.debug('Current Category Index:', state.currentCategoryIndex);
      console.debug('Active Category IDs:', state.activeCategoryIds);
      console.debug('Nutrition Onboarding ID:', state.nutritionOnboardingId);
      console.debug('========================================');
    },
    resetCategoryAnswers: (state, { payload }) => {
      const categoryId = payload;
      if (state.questionsByCategory[categoryId]) {
        state.questionsByCategory[categoryId].forEach(question => {
          question.customer_answer = [];
        });
      }
    },
    setSavingProgress: (state, { payload }) => {
      state.savingProgress = payload;
    },
    setValidationErrors: (state, { payload }) => {
      state.validationErrors = payload;
    },
    clearValidationError: (state, { payload }) => {
      const questionId = payload;
      if (state.validationErrors[questionId]) {
        delete state.validationErrors[questionId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      //HYDRATE
      .addCase(HYDRATE, (state, { payload }) => ({
        ...state,
        ...payload.nutritionOnboarding,
      }))
      // GET NUTRITION ONBOARDING DATA
      .addCase(actions.getNutritionOnboardingData.pending, (state) => ({
        ...state,
        isLoading: true,
        success: false,
        error: "",
      }))
      .addCase(
        actions.getNutritionOnboardingData.fulfilled,
        (state, { payload }) => {
          // Sort steps by sequence_no before storing
          const stepsData = payload?.data?.steps || {};
          if (stepsData.data && Array.isArray(stepsData.data)) {
            stepsData.data = stepsData.data.sort((a, b) => a.sequence_no - b.sequence_no);
          }
          
          return {
            ...state,
            isLoading: false,
            success: true,
            data: payload?.data,
            planDetails: payload?.data?.plan_detail || [],
            nutritionistData: payload?.data?.footer_card?.data || {},
            stepsData: stepsData,
            footerCard: payload?.data?.footer_card || {},
            nutritionOnboardingId: payload?.data?.nutrition_onboarding_id || null,
          };
        }
      )
      .addCase(
        actions.getNutritionOnboardingData.rejected,
        (state, { payload }) => ({
          ...state,
          isLoading: false,
          error: payload || "",
        })
      )
      // GET QUESTION MODULES
      .addCase(actions.getQuestionModules.pending, (state) => ({
        ...state,
        questionModulesLoading: true,
        questionModulesSuccess: false,
        questionModulesError: "",
      }))
      .addCase(
        actions.getQuestionModules.fulfilled,
        (state, { payload }) => {
          const results = payload?.data?.results || [];
          // Filter by is_active and sort by ranking
          const activeResults = results
            .filter(item => item.is_active)
            .sort((a, b) => a.ranking - b.ranking);
          const nextModule = activeResults.find(item => !item.is_module_completed);
          
          return {
            ...state,
            questionModulesLoading: false,
            questionModulesSuccess: true,
            questionModulesData: payload?.data,
            questionTypes: activeResults,
            nextQuestionModuleId: nextModule?.id || null,
            activeCategoryIds: activeResults.map(item => item.id),
          };
        }
      )
      .addCase(
        actions.getQuestionModules.rejected,
        (state, { payload }) => ({
          ...state,
          questionModulesLoading: false,
          questionModulesError: payload || "",
        })
      )
      // GET QUESTIONS FOR CATEGORY
      .addCase(actions.getQuestionsForCategory.pending, (state) => ({
        ...state,
        questionsLoading: true,
        questionsError: "",
      }))
      .addCase(
        actions.getQuestionsForCategory.fulfilled,
        (state, { payload }) => {
          const { categoryId, data } = payload;
          const results = data?.results || [];
          const activeQuestions = results
            .filter(question => question.is_active)
            .sort((a, b) => a.ranking - b.ranking);
          
          return {
            ...state,
            questionsLoading: false,
            questionsByCategory: {
              ...state.questionsByCategory,
              [categoryId]: activeQuestions,
            },
          };
        }
      )
      .addCase(
        actions.getQuestionsForCategory.rejected,
        (state, { payload }) => ({
          ...state,
          questionsLoading: false,
          questionsError: payload || "",
        })
      )
      // SAVE CATEGORY ANSWERS
      .addCase(actions.saveCategoryAnswers.pending, (state) => ({
        ...state,
        savingAnswers: true,
        savingError: "",
        savingProgress: { current: 0, total: 0 },
      }))
      .addCase(
        actions.saveCategoryAnswers.fulfilled,
        (state) => ({
          ...state,
          savingAnswers: false,
          savingProgress: { current: 0, total: 0 },
        })
      )
      .addCase(
        actions.saveCategoryAnswers.rejected,
        (state, { payload }) => ({
          ...state,
          savingAnswers: false,
          savingError: payload || "",
        })
      )
      // SUBMIT ONBOARDING FORM
      .addCase(actions.submitOnboardingForm.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(
        actions.submitOnboardingForm.fulfilled,
        (state, { payload }) => ({
          ...state,
          isLoading: false,
          nutritionistData: payload?.data?.nutritionist_details || {},
        })
      )
      .addCase(
        actions.submitOnboardingForm.rejected,
        (state, { payload }) => ({
          ...state,
          isLoading: false,
          error: payload || "",
        })
      );
  },
});

export { getNutritionOnboardingData, getQuestionModules, getQuestionsForCategory, saveCategoryAnswers, submitOnboardingForm } from "./Actions";

export const {
  setCategories,
  setCurrentCategoryIndex,
  updateAnswer,
  resetCategoryAnswers,
  setSavingProgress,
  setValidationErrors,
  clearValidationError,
} = nutritionOnboardingSlice.actions;

// Export selectors
export * from './selectors';

export default nutritionOnboardingSlice.reducer;